import {
  ContentType,
  MessageExtractKind,
  MessageExtractOutcome,
  MessageExtractStatus,
} from './enums';
import type { OwnerScope } from '@prisma/client';
import { z } from 'zod';
import { browsableContentTypesSchema } from './content-browsable';
import type { ConfigChangeSummary } from './provenance-summary.js';

export const contentHierarchyModeSchema = z.enum(['ALL', 'ROOT_ONLY', 'CHILD_ONLY']);
export type ContentHierarchyMode = z.infer<typeof contentHierarchyModeSchema>;
export const documentHierarchyModeSchema = contentHierarchyModeSchema;
export type DocumentHierarchyMode = ContentHierarchyMode;
export const messageHierarchyModeSchema = contentHierarchyModeSchema;
export type MessageHierarchyMode = ContentHierarchyMode;

export const contentHierarchySectionSchema = z.object({
  hierarchyMode: contentHierarchyModeSchema.optional(),
}).strict();

export const documentFilterDefinitionSchema = contentHierarchySectionSchema;
export const messageFilterDefinitionSchema = contentHierarchySectionSchema;

export const contentFilterSectionSchemaMap = {
  document: documentFilterDefinitionSchema.optional(),
  message: messageFilterDefinitionSchema.optional(),
} as const;
export type ContentFilterSectionKey = keyof typeof contentFilterSectionSchemaMap;
export type ContentFilterSectionValue = z.infer<typeof contentHierarchySectionSchema>;

export const contentFilterSectionFieldMap = {
  document: ['hierarchyMode'],
  message: ['hierarchyMode'],
} as const satisfies Record<ContentFilterSectionKey, ReadonlyArray<keyof ContentFilterSectionValue>>;
export const contentFilterSectionKeys = Object.keys(
  contentFilterSectionFieldMap
) as ContentFilterSectionKey[];

export const contentFilterSectionsSchema = z.object(contentFilterSectionSchemaMap).strict();
export type ContentFilterSections = z.infer<typeof contentFilterSectionsSchema>;

export const contentListProjectionSchema = z.enum(['CONTENT', 'CONVERSATION']);
export type ContentListProjection = z.infer<typeof contentListProjectionSchema>;

export const obligationFilterSchema = z.object({
  kinds: z.array(z.nativeEnum(MessageExtractKind)).optional(),
  statuses: z.array(z.nativeEnum(MessageExtractStatus)).optional()
    .describe(
      "Statuses to match; when omitted, open obligations (OPEN, CLARIFICATION) are assumed, the same set the row badge counts. Name RESOLVED or CANCELLED explicitly to include closed ones."
    ),
  outcomes: z.array(z.nativeEnum(MessageExtractOutcome)).optional(),
  role: z.enum(['ACTIONEE', 'ORIGINATOR', 'INVOLVED', 'UNASSIGNED', 'ANY']).optional()
    .describe(
      "Viewer-relative direction: ACTIONEE = assigned to the viewing member (on their plate), ORIGINATOR = assigned to someone else on a thread the viewer participates in (waiting on others), INVOLVED = ACTIONEE or ORIGINATOR (everything they owe plus everything they're waiting on), UNASSIGNED = no assignee, ANY = no direction constraint"
    ),
  dueBefore: z.string().datetime().optional(),
  dueAfter: z.string().datetime().optional(),
  overdue: z.boolean().optional(),
  hasDueDate: z.boolean().optional(),
  // Viewer-relative (like role): evaluated against the VIEWING member's latest
  // MEMBER_PRIORITY_SET signal per extract; a latest null (cleared) counts as not set.
  // The shared computedScore is deliberately NOT a predicate — it participates via
  // orderBy 'importance' only and is never member-facing.
  hasPersonalPriority: z.boolean().optional(),
  personalPriorityAtMost: z.number().int().min(1).max(10).optional(),
}).strict();
export type ObligationFilter = z.infer<typeof obligationFilterSchema>;

export const filterDefinitionBaseSchema = z.object({
  contentTypes: browsableContentTypesSchema.optional(),
  sections: contentFilterSectionsSchema.optional(),
  obligations: obligationFilterSchema.optional(),
  createdByMemberIds: z.array(z.number().int().positive()).optional(),
  tagIds: z.array(z.number().int().positive()).optional(),
  search: z.string().optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
  isRead: z.boolean().optional(),
  isStarred: z.boolean().optional(),
  isFollowing: z.boolean().optional(),
  orderBy: z.enum(['timelineAt', 'createdAt', 'updatedAt', 'title', 'importance', 'relevance']).optional(),
  orderDir: z.enum(['asc', 'desc']).optional(),
  tabLabel: z.string().optional(),
  tabLabelMobile: z.string().optional(),
}).strict();

const filterDefinitionParsedSchema = filterDefinitionBaseSchema.extend({
  projection: contentListProjectionSchema.default('CONTENT'),
}).strict();

type FilterDefinitionParsed = z.infer<typeof filterDefinitionParsedSchema>;

function isMessageOnly(contentTypes: ContentType[] | undefined): boolean {
  return contentTypes?.length === 1 && contentTypes[0] === ContentType.MESSAGE;
}

// Saved filters display like the seeded tabs by default: Documents and Messages collapse
// to thread/tree roots unless the definition explicitly chooses ALL or CHILD_ONLY, and a
// MESSAGE-only filter renders as conversation cards (the Comms-tab display) rather than
// flat message rows. Defaults are injected at parse time so both stored definitions
// (serializeFilter) and new writes (create/update tools and routes) carry them without
// per-caller logic.
export const filterDefinitionSchema = filterDefinitionParsedSchema.superRefine((definition, ctx) => {
  if (definition.orderBy === 'importance' && definition.orderDir === 'desc') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['orderDir'],
      message: "orderBy 'importance' is ascending-only (lower = more important); omit orderDir or use 'asc'",
    });
  }
}).transform((definition): FilterDefinitionParsed => ({
  ...definition,
  projection: isMessageOnly(definition.contentTypes) ? 'CONVERSATION' : definition.projection,
  sections: {
    document: { hierarchyMode: definition.sections?.document?.hierarchyMode ?? 'ROOT_ONLY' },
    message: { hierarchyMode: definition.sections?.message?.hierarchyMode ?? 'ROOT_ONLY' },
  },
}));

export type FilterDefinition = z.infer<typeof filterDefinitionSchema>;

export const filterScopeSchema = z.enum(['PRIVATE', 'HUB']);
export type FilterScope = z.infer<typeof filterScopeSchema>;

export const filterCreateSchema = z.object({
  name: z.string().trim().min(1),
  description: z.string().trim().min(1).optional().nullable(),
  scope: filterScopeSchema.default('PRIVATE'),
  definition: filterDefinitionSchema,
}).strict();

export type FilterCreateInput = z.infer<typeof filterCreateSchema>;

export const filterUpdateSchema = z.object({
  name: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).optional().nullable(),
  definition: filterDefinitionSchema.optional(),
  isTab: z.boolean().optional(),
  isDefaultTab: z.boolean().nullable().optional(),
  shortName: z.string().trim().min(1).max(32).nullable().optional(),
}).strict().refine((input) => {
  return (
    input.name !== undefined
    || input.description !== undefined
    || input.definition !== undefined
    || input.isTab !== undefined
    || input.isDefaultTab !== undefined
    || input.shortName !== undefined
  );
}, {
  message: 'At least one field must be provided',
});

export type FilterUpdateInput = z.infer<typeof filterUpdateSchema>;

export const filterReorderSchema = z.object({
  tabIds: z.array(z.number().int().positive()),
  filterIds: z.array(z.number().int().positive()),
}).strict();

export type FilterReorderInput = z.infer<typeof filterReorderSchema>;

export interface Filter {
  id: number;
  createdByMemberId: number;
  ownerMemberId: number | null;
  hubId: number;
  name: string;
  description?: string;
  ownerScope: OwnerScope;
  definition: FilterDefinition;
  isTab: boolean;
  isDefaultTab?: boolean;
  tabOrder?: number;
  filterOrder?: number;
  shortName?: string;
  createdAt: string;
  updatedAt: string;
  lastChange?: ConfigChangeSummary | null;
}
